import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { candidacyService } from '@/shared/services/candidacyService';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import ActionBar from '@/components/common/ActionBar';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import DecisionEmailPreview from '@/features/applications/faculty/DecisionEmailPreview';
import { formatAnswerBy } from '@/shared/utils/deadline';
import { AnswerByField } from './OfferChoiceDialogs';

export const MESSAGE_MAX = 5000;
const PREVIEW_DELAY_MS = 400;

const TITLE_ID = 'offer-dialog-title';
const MESSAGE_ID = 'offer-email-message';

const LOAD_FAILED =
    "The program's offer text couldn't be loaded. The student will get only the sentence that states the offer unless you write a message here.";

// The server refuses a draft with the reason the offer itself would be
// refused for; anything else is a failure to load.
const isRefusal = (err) =>
    [403, 409].includes(err.response?.status) &&
    typeof err.response?.data?.message === 'string';

/**
 * The dialog a reviewer makes an offer in. It opens at once and fills the
 * message box from the program's offer template for this candidacy. The
 * reviewer edits the text; the dialog shows the student's email as the server
 * renders it for that text, and Make the offer sends the box exactly as it
 * stands. When the draft is refused, the dialog says why and offers only
 * Close. When it cannot be loaded, the box starts empty and the student gets
 * the sentence that states the offer plus whatever the reviewer writes.
 * `mode` does not change the dialog: the server decides who may offer.
 */
export default function OfferDialog({
    show,
    candidacy,
    position,
    releaseFrom,
    onHide,
    onOffered,
}) {
    const previewLocked = usePreviewLock();
    // 'loading', 'ready', 'failed' or 'refused'
    const [phase, setPhase] = useState('loading');
    const [draft, setDraft] = useState(null);
    const [refusal, setRefusal] = useState(null);
    const [message, setMessage] = useState('');
    const [respondByDay, setRespondByDay] = useState('');
    // The sentences the email opens with and the day they were rendered
    // for: they state the deadline, so they come from the draft and then
    // from each preview, and show only while they match the chosen day.
    const [fixed, setFixed] = useState(null);
    const [emails, setEmails] = useState([]);
    const [previewStatus, setPreviewStatus] = useState('loading');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const errorRef = useRevealAlert(error);
    const draftRequest = useRef(0);
    const previewRequest = useRef(0);
    const previewAtOnce = useRef(true);

    const candidacyId = candidacy?.id;

    useEffect(() => {
        if (!show || !candidacyId) return undefined;
        const request = ++draftRequest.current;
        setPhase('loading');
        setDraft(null);
        setRefusal(null);
        setMessage('');
        setRespondByDay('');
        setFixed(null);
        setEmails([]);
        setPreviewStatus('loading');
        setSubmitting(false);
        setError(null);
        previewAtOnce.current = true;
        (async () => {
            try {
                const result = releaseFrom
                    ? await candidacyService.getOfferDraft(
                          candidacyId,
                          releaseFrom
                      )
                    : await candidacyService.getOfferDraft(candidacyId);
                if (request !== draftRequest.current) return;
                if (typeof result?.message !== 'string') {
                    setPhase('failed');
                    return;
                }
                setDraft(result);
                setMessage(result.message);
                setRespondByDay(result.respondByDay || '');
                setFixed({
                    day: result.respondByDay || '',
                    paragraphs: result.fixedParagraphs || [],
                });
                setPhase('ready');
            } catch (err) {
                if (request !== draftRequest.current) return;
                if (isRefusal(err)) {
                    setRefusal(err.response.data.message);
                    setPhase('refused');
                } else {
                    setPhase('failed');
                }
            }
        })();
        return () => {
            draftRequest.current += 1;
        };
    }, [show, candidacyId, releaseFrom]);

    const length = message.length;
    const over = length > MESSAGE_MAX;
    const settled = phase === 'ready' || phase === 'failed';

    useEffect(() => {
        if (!show || !settled) return undefined;
        if (previewLocked) {
            setPreviewStatus('locked');
            return undefined;
        }
        if (over) return undefined;
        const run = async () => {
            const request = ++previewRequest.current;
            try {
                const extras = releaseFrom
                    ? [respondByDay || undefined, releaseFrom]
                    : respondByDay
                      ? [respondByDay]
                      : [];
                const answer = await candidacyService.previewOffer(
                    candidacyId,
                    message,
                    ...extras
                );
                if (request !== previewRequest.current) return;
                if (!Array.isArray(answer?.emails)) throw new Error('shape');
                setEmails(answer.emails);
                if (Array.isArray(answer.fixedParagraphs)) {
                    setFixed({
                        day: respondByDay,
                        paragraphs: answer.fixedParagraphs,
                    });
                }
                setPreviewStatus('ready');
            } catch (_err) {
                if (request !== previewRequest.current) return;
                setPreviewStatus('error');
            }
        };
        let timer = null;
        if (previewAtOnce.current) {
            previewAtOnce.current = false;
            setPreviewStatus('loading');
            run();
        } else {
            timer = setTimeout(run, PREVIEW_DELAY_MS);
        }
        return () => {
            clearTimeout(timer);
            previewRequest.current += 1;
        };
    }, [
        show,
        settled,
        candidacyId,
        message,
        respondByDay,
        releaseFrom,
        over,
        previewLocked,
    ]);

    const close = () => {
        if (submitting) return;
        onHide();
    };

    const confirm = async () => {
        setSubmitting(true);
        setError(null);
        let offered;
        try {
            offered = await candidacyService.offer(candidacyId, {
                message,
                ...(respondByDay && { respondByDay }),
                ...(releaseFrom && { releaseFrom }),
            });
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'The offer could not be made. Try again.'
            );
            setSubmitting(false);
            return;
        }
        onOffered(offered);
    };

    const label = position
        ? `${position.subject} ${position.courseNumber} section ${position.section}`
        : 'this section';
    const name = candidacy?.student?.name;
    const countId = `${MESSAGE_ID}-count`;
    const helpId = `${MESSAGE_ID}-help`;

    return (
        <Modal
            show={show}
            size="lg"
            scrollable
            onHide={close}
            aria-labelledby={TITLE_ID}
        >
            <Modal.Header>
                <Modal.Title id={TITLE_ID} as="h2" className="h5">
                    {`Make an offer to ${name}`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{`Offer ${name} the position for ${label}?`}</p>
                {phase === 'loading' && (
                    <div role="status" className="text-body-secondary mb-0">
                        <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                            aria-hidden="true"
                        />
                        Loading the offer text...
                    </div>
                )}
                {phase === 'refused' && (
                    <Alert variant="warning" className="mb-0">
                        {refusal}
                    </Alert>
                )}
                {phase === 'failed' && (
                    <Alert variant="warning">{LOAD_FAILED}</Alert>
                )}
                {settled && (
                    <>
                        {phase === 'ready' && draft.respondByDay && (
                            <AnswerByField
                                id="offer-answer-by"
                                value={respondByDay}
                                onChange={setRespondByDay}
                                min={draft.earliestDay}
                                max={draft.latestDay}
                                disabled={submitting}
                                emptyMeans={
                                    draft.respondBy
                                        ? `The program's usual deadline is ${formatAnswerBy(draft.respondBy)}.`
                                        : "Leave it empty to use the program's usual number of days."
                                }
                            />
                        )}
                        {phase === 'ready' && fixed?.day === respondByDay && (
                            <div className="mb-3">
                                <p className="fw-semibold mb-1">
                                    The email starts with:
                                </p>
                                {fixed.paragraphs.map((paragraph, index) => (
                                    <p key={index} className="mb-1 text-break">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        )}
                        <Form.Group controlId={MESSAGE_ID} className="mb-2">
                            <Form.Label>Message to the student</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={submitting}
                                aria-describedby={`${helpId} ${countId}`}
                            />
                            <Form.Text id={helpId} muted as="div">
                                The student reads this after the sentence that
                                states the offer, then a button to answer it.
                                Leave a blank line between paragraphs.
                            </Form.Text>
                            <Form.Text
                                id={countId}
                                as="div"
                                className={
                                    over ? 'text-danger' : 'text-body-secondary'
                                }
                            >
                                {length.toLocaleString('en-US')} of{' '}
                                {MESSAGE_MAX.toLocaleString('en-US')} characters
                                {over &&
                                    '. This is too long to send. Shorten the message.'}
                            </Form.Text>
                        </Form.Group>
                        <details className="mt-3">
                            <summary className="fw-semibold">
                                Preview the email
                            </summary>
                            <div className="mt-2">
                                {over ? (
                                    <p className="text-body-secondary mb-0">
                                        The preview returns when the message
                                        fits the limit.
                                    </p>
                                ) : (
                                    <DecisionEmailPreview
                                        emails={emails}
                                        status={previewStatus}
                                    />
                                )}
                            </div>
                        </details>
                    </>
                )}
                {error && (
                    <Alert
                        variant="danger"
                        className="mt-3 mb-0"
                        ref={errorRef}
                        tabIndex={-1}
                    >
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer className="flex-column align-items-stretch">
                {settled && <PreviewLockNote locked={previewLocked} />}
                {phase === 'refused' ? (
                    <ActionBar
                        className="w-100"
                        secondary={
                            <Button variant="outline-primary" onClick={close}>
                                Close
                            </Button>
                        }
                    />
                ) : (
                    <ActionBar
                        className="w-100"
                        secondary={
                            <Button
                                variant="outline-primary"
                                onClick={close}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                        }
                        primary={
                            <Button
                                variant="primary"
                                onClick={confirm}
                                disabled={
                                    !settled ||
                                    submitting ||
                                    previewLocked ||
                                    over
                                }
                                aria-describedby={
                                    previewLocked
                                        ? PREVIEW_LOCK_NOTE_ID
                                        : undefined
                                }
                            >
                                Make the offer
                            </Button>
                        }
                    />
                )}
            </Modal.Footer>
        </Modal>
    );
}
